import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormLabel,
    Box,
    LinearProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material'

import LoadingDefault from '../../../Elements/Loading/LoadingDefault'
import { faSave } from '@fortawesome/pro-duotone-svg-icons/faSave'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ConfirmDeleteChangesDialog from '../../../Form/ConfirmDeleteChangesDialog'

const NewFile = (props) => {
    const maxFileSize = 300 * 1024 * 1024
    const chunkSize = 1024 * 1024
    const multiple = props.multiple
    const [fileContent, setFileContent] = useState(
        props.dataSignature ? props.dataSignature : null
    )
    const [name, setName] = useState(props?.name ?? '')
    const [description, setDescription] = useState(props?.description ?? '')
    const [submitting, setSubmitting] = useState(false)
    const [errors, setErrors] = useState(false)

    const [files, setFiles] = useState([])
    const [fileData, setFileData] = useState([])
    const [uploadProgress, setUploadProgress] = useState(0)
    const [uploading, setUploading] = useState(false)
    const [showConfirmChanges, setShowConfirmChanges] = useState(false)
    const [unsavedChanges, setUnsavedChanges] = useState(false)

    const hasFormErrors = (response) => {
        return (
            response.response.status === 400 &&
            response.response.data &&
            response.response.data.errors
        )
    }

    const handleSuccess = (response, item) => {
        let newFile = {}

        if (!item) {
            newFile = {
                id: response.data.id,
                filename: name,
                description: description,
            }
        } else {
            newFile = {
                id: response.data.id,
                filename: item.name,
                description: item.description,
            }
        }

        setSubmitting(false)
        setUploading(false)
        setErrors(false)
        setUploadProgress(0)
        props.onSuccess(newFile)
    }

    const handleErrors = (response) => {
        if (!hasFormErrors(response)) {
            setErrors({ generic: 'Something went wrong.' })
        } else {
            setErrors(response.response.data.errors)
        }

        setSubmitting(false)
        setUploading(false)
        setUploadProgress(0)
    }

    const handleChange = (event) => {
        const { name, value } = event.target
        if (name === 'name') setName(value)
        if (name === 'description') setDescription(value)
    }

    const validateFile = (file) => {
        const errs = {}
        if (file.size > maxFileSize) {
            errs.size = `File size exceeds ${
                maxFileSize / (1024 * 1024)
            }MB limit.`
        }
        if (props.accepts && !isAcceptedType(file.type)) {
            errs.type = 'Unsupported file type.'
        }
        return errs
    }

    const isAcceptedType = (mimeType) => {
        if (!props.accepts) return true
        const acceptTypes = getAccepts(props.accepts).split(',')
        return acceptTypes.some(
            (type) =>
                mimeType.includes(type.trim().split('/')[0]) ||
                mimeType === type.trim()
        )
    }

    const readFileInChunks = async (file) => {
        const chunks = []
        let offset = 0

        while (offset < file.size) {
            const chunk = file.slice(offset, offset + chunkSize)
            const arrayBuffer = await new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsArrayBuffer(chunk)
            })
            chunks.push(arrayBuffer)
            offset += chunkSize

            setUploadProgress(Math.round((offset / file.size) * 50))
        }
        return new Blob(chunks)
    }

    const handleFileChange = async (e) => {
        e.preventDefault()
        const file = e.target.files[0]
        if (!file) return

        const validationErrors = validateFile(file)
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors)
            return
        }

        setSubmitting(true)
        setUploadProgress(0)
        setErrors(false)
        setUploading(false)

        try {
            let processedFile = file
            if (file.size > 50 * 1024 * 1024) {
                processedFile = await readFileInChunks(file)
            }

            setFileContent(processedFile)
            setName(file.name)
            setSubmitting(false)
            setUploading(false)
            setUploadProgress(0)
        } catch (error) {
            setErrors({ generic: 'Error processing file.' })
            setSubmitting(false)
            setUploadProgress(0)
        }
    }

    const getAccepts = (accepts) => {
        let acceptTypes = ''
        accepts.map((item) => {
            if (item === 'images') {
                acceptTypes +=
                    (acceptTypes ? ',' : '') +
                    'image/x-png,image/gif,image/jpeg'
            } else if (item === 'documents') {
                acceptTypes +=
                    (acceptTypes ? ',' : '') +
                    'application/msword,text/plain, application/pdf'
            } else if (item === 'spreadsheets') {
                acceptTypes +=
                    (acceptTypes ? ',' : '') + 'application/vnd.ms-excel'
            }
        })
        return acceptTypes
    }

    const handleCloseModal = (changes) => {
        if (changes) {
            setShowConfirmChanges(true)
            return renderRemoveChangesForm()
        }
        return props.onClose()
    }

    const renderRemoveChangesForm = () => {
        if (!showConfirmChanges) return null

        return (
            <ConfirmDeleteChangesDialog
                kedo={props.kedo}
                variant={'modal'}
                accept={props.onClose}
                cancel={() => setShowConfirmChanges(false)}
            />
        )
    }

    useEffect(() => {
        setUnsavedChanges(true)
    }, [name, description])

    const handleMultipleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files)
        const fd = selectedFiles.map((file) => ({
            file,
            name: file.name,
            description: '',
        }))
        setFiles(selectedFiles)
        setFileData(fd)
    }

    const handleFileNameChange = (e, index) => {
        const newFD = [...fileData]
        newFD[index].name = e.target.value

        let errs = { ...errors }
        if (e.target.value) delete errs[index]

        setFileData(newFD)
        setErrors(errs)
    }

    const handleFileDescriptionChange = (e, index) => {
        const newFD = [...fileData]
        newFD[index].description = e.target.value
        setFileData(newFD)
    }

    const submitFile = () => {
        let errs = {}

        if (!multiple) {
            if (!name || !fileContent) {
                if (!name || name.length <= 0)
                    errs.name = props.kedo.t('This is a required field')
                if (!fileContent)
                    errs.fileContent = props.kedo.t('This is a required field')

                setErrors(errs)
                return
            }

            setSubmitting(true)
            setUploading(true)
            setErrors(false)
            setUploadProgress(0)

            const formData = new FormData()

            formData.append('file', fileContent)
            formData.append(
                'metadata',
                JSON.stringify({
                    name,
                    description: description || '',
                    environment: props.kedo.env().getEnvironment().id,
                })
            )

            // Signatures get send as json
            if (props.dataSignature) {
                formData.append(
                    'environment',
                    props.kedo.env().getEnvironment().id
                )
                formData.append('name', description || '')
                formData.append('description', description || '')
            }

            props.kedo
                .api()
                .post(props.kedo.api().getFileEndpoint(), formData, {
                    headers: {
                        'Content-Type': props.dataSignature
                            ? 'application/json'
                            : 'multipart/form-data',
                    },
                    onUploadProgress: (e) => {
                        setUploadProgress(
                            Math.round((e.loaded / e.total) * 100)
                        )
                    },
                })
                .then((res) => handleSuccess(res))
                .catch((err) => handleErrors(err))
        } else {
            let localErrors = { ...errors }
            if (!fileData || fileData.length <= 0) {
                localErrors.fileContent = props.kedo.t(
                    'This is a required field'
                )
                setErrors(localErrors)
                return
            }

            setSubmitting(true)
            setUploading(true)
            setErrors(false)
            setUploadProgress(0)

            let hasErrors = false

            fileData.forEach((file, index) => {
                if (!file.name || file.name.length === 0) {
                    localErrors[index] = {
                        name: props.kedo.t('This is a required field'),
                    }
                    hasErrors = true
                }
            })

            if (hasErrors) {
                setErrors(localErrors)
                setSubmitting(false)
                return
            }

            fileData.forEach((file) => {
                const formData = new FormData()
                formData.append('name', file.name)
                formData.append('description', file.description || '')
                formData.append('file', file.file)
                formData.append(
                    'environment',
                    props.kedo.env().getEnvironment().id
                )

                props.kedo
                    .api()
                    .post(props.kedo.api().getFileEndpoint(), formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        onUploadProgress: (e) => {
                            setUploadProgress(
                                Math.round((e.loaded / e.total) * 100)
                            )
                        },
                    })
                    .then((res) => handleSuccess(res, file))
                    .catch((err) => handleErrors(err))
            })
        }
    }

    const renderErrorFeedback = (msg) => {
        if (!msg) return null
        return (
            <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                {msg}
            </Box>
        )
    }

    return (
        <div>
            {showConfirmChanges ? renderRemoveChangesForm() : null}

            <Dialog
                open={true}
                onClose={() => handleCloseModal(unsavedChanges)}
                fullWidth
                maxWidth={multiple ? 'lg' : 'sm'}
                sx={{
                    '& .MuiDialog-container': {
                        alignItems: 'flex-start',
                    },
                    '& .MuiDialog-paper': {
                        transform: 'none',
                        '@media (min-width: 576px)': {
                            maxWidth: 500,
                            margin: '1.75rem auto',
                        },
                        '@media (min-width: 992px)': {
                            maxWidth: 800,
                        },
                    },
                }}
            >
                <DialogTitle>{props.kedo.t('New file')}</DialogTitle>

                <DialogContent dividers>
                    {(submitting || uploading) && (
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                            />
                        </Box>
                    )}

                    {!multiple ? (
                        <Box component="form">
                            {!props.dataSignature ? (
                                <Box sx={{ mb: 2 }}>
                                    <FormLabel className="required">
                                        {props.kedo.t('File')}
                                    </FormLabel>
                                    <section
                                        className={
                                            'fakeInputField ' +
                                            (errors && !fileContent
                                                ? 'fake-feedback'
                                                : '')
                                        }
                                    >
                                        <input
                                            name={'file'}
                                            accept={
                                                props.accepts
                                                    ? getAccepts(props.accepts)
                                                    : undefined
                                            }
                                            onChange={handleFileChange}
                                            type="file"
                                        />
                                    </section>
                                    {errors &&
                                        errors.fileContent &&
                                        renderErrorFeedback(errors.fileContent)}
                                    {errors &&
                                        errors.size &&
                                        renderErrorFeedback(errors.size)}
                                    {errors &&
                                        errors.type &&
                                        renderErrorFeedback(errors.type)}
                                </Box>
                            ) : null}

                            <FormLabel className="required">
                                {props.kedo.t('Name')}
                            </FormLabel>
                            <TextField
                                name="name"
                                fullWidth
                                value={name || ''}
                                onChange={handleChange}
                                error={!!(errors && errors.name)}
                                helperText={errors && errors.name}
                                disabled={submitting}
                                sx={{
                                    mb: 2,
                                    '& .MuiInputBase-input': {
                                        padding: (theme) =>
                                            `${theme.spacing(
                                                1
                                            )} ${theme.spacing(1.5)}`,
                                        height: 'auto',
                                    },
                                }}
                            />

                            <FormLabel>{props.kedo.t('Description')}</FormLabel>
                            <TextField
                                name="description"
                                fullWidth
                                multiline
                                maxRows={3}
                                onChange={handleChange}
                                disabled={submitting}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        padding: 0,
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        padding: (theme) =>
                                            `${theme.spacing(
                                                1
                                            )} ${theme.spacing(1.5)}`,
                                        height: 'auto',
                                        boxSizing: 'border-box', // important for height calculations
                                        lineHeight: 1.5,
                                    },
                                }}
                            />
                        </Box>
                    ) : (
                        <Box component="form">
                            <Box sx={{ mb: 2 }}>
                                <FormLabel className="required">
                                    {props.kedo.t('Files')}
                                </FormLabel>
                                <section
                                    className={
                                        'fakeInputField ' +
                                        (errors &&
                                        (!Array.isArray(fileData) ||
                                            fileData.length === 0)
                                            ? 'fake-feedback'
                                            : '')
                                    }
                                >
                                    <input
                                        name="files"
                                        accept={
                                            props.accepts
                                                ? getAccepts(props.accepts)
                                                : undefined
                                        }
                                        onChange={handleMultipleFileChange}
                                        type="file"
                                        multiple
                                    />
                                </section>
                                {errors &&
                                    errors.fileContent &&
                                    renderErrorFeedback(errors.fileContent)}
                            </Box>

                            {multiple && files.length > 0 && (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <FormLabel>
                                                    {props.kedo.t('File Name')}
                                                </FormLabel>
                                            </TableCell>
                                            <TableCell>
                                                <FormLabel className="required">
                                                    {props.kedo.t('New Name')}
                                                </FormLabel>
                                            </TableCell>
                                            <TableCell>
                                                <FormLabel>
                                                    {props.kedo.t(
                                                        'Description'
                                                    )}
                                                </FormLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {fileData.map((file, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <FormLabel>
                                                        {file.file.name}
                                                    </FormLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={file.name}
                                                        onChange={(e) =>
                                                            handleFileNameChange(
                                                                e,
                                                                index
                                                            )
                                                        }
                                                        error={
                                                            errors[index] &&
                                                            !!errors[index].name
                                                        }
                                                        helperText={
                                                            errors[index] &&
                                                            errors[index].name
                                                        }
                                                        fullWidth
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={file.description}
                                                        onChange={(e) =>
                                                            handleFileDescriptionChange(
                                                                e,
                                                                index
                                                            )
                                                        }
                                                        fullWidth
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={submitFile}
                        variant="contained"
                        sx={{ fontSize: 'medium' }}
                    >
                        {submitting ? (
                            <LoadingDefault size={'sm'} />
                        ) : (
                            <FontAwesomeIcon icon={faSave} />
                        )}
                        &nbsp; {props.kedo.t('Save')}
                    </Button>

                    <Button
                        onClick={props.onClose}
                        variant="outlined"
                        sx={{ fontSize: 'medium' }}
                    >
                        {props.kedo.t('Cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default NewFile
